/* eslint-disable react/no-danger */
import dynamic from 'next/dynamic';
import { useEffect, useMemo } from 'react';
import { actions, GET_DETAIL_PROJECT } from '@containers/Home/slice';
import { useRouter } from 'next/router';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectConfigLittlePlanetProject,
  selectConfigFirstSceneProject,
  selectMetaDataProject,
  selectTitleProject,
  selectListScene,
  selectToken,
} from '@containers/Home/selectors';
import { NextSeo } from 'next-seo';
import {
  LIST_FAVICON_DEFAULT,
  LIST_LINK_STYLE_SHEET,
} from '@containers/Home/constants';
import { useGetTenant } from '@containers/Home/hook/useGetTenant';

const Home = dynamic(() => import('containers/Home'), {
  loading: () => <span>Loading</span>,
});

function PreviewContainer() {
  const router = useRouter();
  const dispatch = useDispatch();

  const token = useSelector(selectToken);
  const title = useSelector(selectTitleProject);
  const listScene = useSelector(selectListScene);
  const metadata = useSelector(selectMetaDataProject);
  const first_scene = useSelector(selectConfigFirstSceneProject);
  const config_little_planet = useSelector(selectConfigLittlePlanetProject);

  const { tenant, onGetTenantByDomain } = useGetTenant({ host: '' });

  const detailFirstScene = useMemo(
    () => listScene.filter(item => item.id === first_scene)[0] || undefined,
    [listScene, first_scene],
  );

  // NOTE: set token
  useEffect(() => {
    if (tenant) {
      if (!token && router.query.token && router.query.tour) {
        dispatch(
          actions.setToken(JSON.parse(JSON.stringify(router.query.token))),
        );
        router.push(
          `${process.env.NEXT_PUBLIC_BASE_URL_PREVIEW}?tour=${
            router.query.tour
          }&scene=${router.query.scene || ''}`,
          undefined,
          { shallow: true },
        );
      } else if (
        !token &&
        !router.query.token &&
        (router.query.scene || router.query.tour)
      ) {
        window.open(
          `${tenant?.domain_studio}/redirect${
            process.env.NEXT_PUBLIC_BASE_URL_PREVIEW
          }/${router.query.tour}/${router.query.scene || ''}`,
          '_self',
        );
      }
    } else onGetTenantByDomain();
  }, [
    token,
    tenant,
    router,
    dispatch,
    router.query.tour,
    router.query.scene,
    router.query.token,
    onGetTenantByDomain,
  ]);

  useEffect(() => {
    if (token)
      dispatch(
        GET_DETAIL_PROJECT({
          id: router.query.tour,
          isPublic: false,
          token,
        }),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    if (listScene && listScene.length) {
      let scene = detailFirstScene
        ? { ...detailFirstScene }
        : { ...listScene[0] };
      if (router.query.scene)
        scene =
          listScene.filter(
            item =>
              item.slug === router.query.scene ||
              item.id === router.query.scene,
          )[0] || listScene[0];

      // NOTE: add intro little planet for first scene loaded
      scene.config = scene.config ? { ...scene?.config } : {};
      scene.config = {
        ...scene.config,
        isLittlePlanet: config_little_planet?.enable || false,
        timeTransitionLittlePlanet:
          config_little_planet?.transition_time || 2.0,
      };
      dispatch(actions.setSceneSelected(scene));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listScene.length]);

  return (
    <>
      {tenant && (
        <NextSeo
          title={metadata?.title || title}
          description={metadata?.description || tenant?.description}
          additionalMetaTags={[
            {
              property: 'name',
              content: tenant?.name,
            },
            {
              name: 'application-name',
              content: tenant?.name,
            },
          ]}
          additionalLinkTags={
            metadata?.favicon || !tenant?.domain_public?.includes('panoee')
              ? [
                  ...[
                    {
                      rel: 'shortcut icon',
                      href: metadata?.favicon || tenant?.favicon?.src,
                    },
                  ],
                  ...(tenant?.domain_public?.includes('panoee')
                    ? LIST_LINK_STYLE_SHEET
                    : []),
                ]
              : [...LIST_FAVICON_DEFAULT, ...LIST_LINK_STYLE_SHEET]
          }
          openGraph={{
            type: 'website',
            url: tenant?.domain_public + router.asPath,
            title: metadata?.title || title,
            description: metadata?.description || tenant?.description,
            site_name: tenant?.name,
            profile: {
              username: router.query.username as string,
            },
          }}
          facebook={{
            appId: process.env.NEXT_PUBLIC_FB_APP_ID || '2086263614924092',
          }}
        />
      )}
      {token && <Home isPublic={false} />}
    </>
  );
}

export default PreviewContainer;
